import React, { useEffect, useRef, useState } from "react";
import "./UnlockSlider.scss";

interface IProps {
    locked: boolean
}

interface IDragState {
    x: number,
    correction: number,
    dragging: boolean
}

export function UnlockSlider({ locked }: IProps) {
    const sliderRef = useRef<HTMLDivElement>(null);
    const [ dragState, setDragState ] = useState<IDragState>({correction: 0, x: 0, dragging: false});

    // On init, set the max range
    useEffect(() => {
        if (!sliderRef.current) return;
        const slider = sliderRef.current;
        const sliderRect = slider.getBoundingClientRect();

        const track = slider.parentElement as HTMLDivElement;
        const trackRect = track.getBoundingClientRect();

        slider.style.setProperty('--max-slider-slide-width', `${trackRect.width - sliderRect.width}px`)

    }, [sliderRef])

    // Set state on drag
    useEffect(() => {
        if (!sliderRef.current) return;
        const slider = sliderRef.current;
        const handleDragStart = (e: PointerEvent) => {
            const sliderRect = slider.getBoundingClientRect();
            slider.classList.add('dragging');
            setDragState({
                x: e.clientX,
                correction: e.clientX - sliderRect.left,
                dragging: true,
            })
        }
        const handleDrag = (e: PointerEvent) => {
            if (!dragState.dragging) return;
            setDragState((prevState) => {
                return {...prevState, x: e.clientX};
            })
        }
        const handleDragEnd = () => {
            if (!dragState.dragging) return;
            slider.classList.remove('dragging');
            slider.removeAttribute('style')
            setDragState((prevState) => {
                return {...prevState, dragging: false};
            })
        }
        slider.addEventListener('pointerdown', handleDragStart);
        window.addEventListener('pointermove', handleDrag);
        window.addEventListener('pointerup', handleDragEnd);
        return () => {
            slider.removeEventListener('pointerdown', handleDragStart);
            window.removeEventListener('pointermove', handleDrag);
            window.removeEventListener('pointerup', handleDragEnd);
        }
    }, [sliderRef, dragState]);

    // Move the slider after state change
    useEffect(() => {
        if (!sliderRef.current) return;
        if (!dragState.dragging) return;

        const slider = sliderRef.current;
        const sliderRect = slider.getBoundingClientRect();

        const track = slider.parentElement as HTMLDivElement;
        const trackRect = track.getBoundingClientRect();

        const x = dragState.x - dragState.correction - trackRect.left;
        const percentage = Math.min(1, Math.max(0, x / trackRect.width));
        const translateX = `calc(${percentage} * ${trackRect.width - sliderRect.width}px)`
        slider.style.transform = `translateX(${translateX})`;
    }, [dragState, sliderRef]);

    return (
        <div className={`unlock-slider-wrapper ${locked ? 'locked' : ''}`}>
            <div className="track">
                <div className="slider" ref={sliderRef} />
            </div>
            <div className="slider-background">
                <svg xmlns="http://www.w3.org/2000/svg" width="103" height="23" viewBox="0 0 103 23">
                    <text id="UNLOCK" transform="translate(61 18)" fill="#fff" fontSize="18" fontFamily="FranklinGothicATF-Medium, Franklin Gothic ATF" fontWeight="500" letterSpacing="0.05em"><tspan x="-41.364" y="0">UNLOCK</tspan></text>
                    <path id="icon_lock" data-name="icon/lock" d="M207.836,67.5V64.372a4.375,4.375,0,1,0-8.749,0V67.5h-3.122v10.5a14.9,14.9,0,0,0,4.75,1.755q.242.044.489.082h0a15.074,15.074,0,0,0,2,.167h.255a14.927,14.927,0,0,0,7.5-2V67.5Zm-2.81,7.185a1.562,1.562,0,1,1-3.124,0V72.807a1.562,1.562,0,1,1,3.124,0Zm.313-7.185h-3.75V64.372a1.875,1.875,0,1,1,3.75,0Z" transform="translate(-195.966 -57.997)" fill="#fff"/>
                </svg>
            </div>
        </div>
    )
}
